import { useState } from 'react'
import { ArrowLeft, ArrowUpRight, Check, Clock3, ExternalLink, Share2, ShieldCheck } from 'lucide-react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { ArticleCard } from '../components/ArticleCard'
import { ArticleVisual } from '../components/ArticleVisual'
import { PageMeta } from '../components/PageMeta'
import { blogPosts } from '../data/blogs'
import { seoConfig } from '../data/seo'

export default function ArticlePage() {
  const [copied, setCopied] = useState(false)
  const { slug } = useParams()
  const post = blogPosts.find((item) => item.slug === slug)
  if (!post) return <Navigate to="/insights" replace />
  const related = blogPosts.filter((item) => item.slug !== post.slug && item.category === post.category).sort((a, b) => b.published.localeCompare(a.published)).slice(0, 2)
  const published = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${post.published}T00:00:00Z`))
  const canonicalUrl = `${seoConfig.siteUrl}/insights/${post.slug}`
  const takeaways = post.sections.find((section) => section.bullets)?.bullets?.slice(0, 4) ?? []
  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.excerpt,
      datePublished: post.published,
      dateModified: post.published,
      mainEntityOfPage: canonicalUrl,
      image: `${seoConfig.siteUrl}/images/health-insights-library.webp`,
      author: { '@type': 'Organization', name: 'Dr. Fathima Finu Health Library', url: `${seoConfig.siteUrl}/editorial-policy` },
      publisher: { '@type': 'Person', name: 'Dr. Fathima Finu', url: `${seoConfig.siteUrl}/about` },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: seoConfig.siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Health insights', item: `${seoConfig.siteUrl}/insights` },
        { '@type': 'ListItem', position: 3, name: post.title, item: canonicalUrl },
      ],
    },
  ]

  async function shareArticle() {
    try {
      if (navigator.share) {
        await navigator.share({ title: post!.title, text: post!.excerpt, url: canonicalUrl })
        return
      }
      await navigator.clipboard.writeText(canonicalUrl)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2200)
    } catch {
      setCopied(false)
    }
  }

  return (
    <>
      <PageMeta title={post.title} description={post.excerpt} path={`/insights/${post.slug}`} image="/images/health-insights-library.webp" imageAlt="Colorful editorial health library with prevention, movement, nutrition and sleep motifs" type="article" publishedAt={post.published} modifiedAt={post.published} structuredData={structuredData} />
      <article className={`article-page tone-${post.imageTone}`}>
        <header className="article-header">
          <div className="container article-header-inner">
            <Link className="back-link" to="/insights"><ArrowLeft size={17} /> All insights</Link>
            <div className="article-kicker"><span>{post.category}</span><span><Clock3 size={15} /> {post.readTime}</span></div>
            <h1>{post.title}</h1>
            <p>{post.excerpt}</p>
            <div className="article-byline"><span className="byline-avatar">FH</span><span><strong>Fathima Finu Health Library</strong><small><time dateTime={post.published}>Published and source-checked {published}</time></small></span></div>
            <div className="article-hero-art"><ArticleVisual post={post} variant="hero" /></div>
          </div>
        </header>
        <div className="container article-layout">
          <aside className="article-aside">
            <div className="sticky-note"><ShieldCheck size={22} /><strong>Before you read</strong><p>This guide is general education, not a diagnosis or personal treatment plan.</p></div>
            <div className="article-share">
              <span>Share this guide</span>
              <button type="button" onClick={() => void shareArticle()}>{copied ? <Check size={17} /> : <Share2 size={17} />}{copied ? 'Link copied' : 'Share or copy'}</button>
              <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(canonicalUrl)}`} target="_blank" rel="noreferrer">LinkedIn <ArrowUpRight size={14} /></a>
              <a href={`https://wa.me/?text=${encodeURIComponent(`${post.title} ${canonicalUrl}`)}`} target="_blank" rel="noreferrer">WhatsApp <ArrowUpRight size={14} /></a>
            </div>
          </aside>
          <div className="article-content">
            {takeaways.length > 0 && <section className="article-at-a-glance"><span className="eyebrow">At a glance</span><h2>The useful points to carry forward</h2><ul>{takeaways.map((item) => <li key={item}><Check size={17} />{item}</li>)}</ul></section>}
            {post.sections.map((section) => (
              <section className={/(urgent|emergency|red flag|act even|when in doubt)/i.test(section.heading) ? 'article-urgent-section' : undefined} key={section.heading}>
                <h2>{section.heading}</h2>
                {section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                {section.bullets && <ul>{section.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}</ul>}
              </section>
            ))}
            <div className="article-source">
              <span className="eyebrow">Source & further reading</span>
              <a href={post.sourceUrl} target="_blank" rel="noreferrer">{post.sourceLabel} <ExternalLink size={17} /></a>
              <p>Guidance can change. This source was last checked for this article on {published}. See the <Link className="inline-policy-link" to="/editorial-policy">editorial policy</Link>.</p>
            </div>
            <div className="article-next-step">
              <div><strong>Still have questions?</strong><p>Bring your symptoms, medicine list, and priorities to an individual consultation.</p></div>
              <Link className="button" to="/booking">Request a visit <ArrowUpRight size={17} /></Link>
            </div>
          </div>
        </div>
      </article>

      {related.length > 0 && <section className="section related-section"><div className="container"><span className="eyebrow">Keep reading</span><h2>Related health guides</h2><div className="article-grid related-grid">{related.map((item) => <ArticleCard key={item.slug} post={item} />)}</div></div></section>}
    </>
  )
}
